import React, { useEffect, useState } from 'react';
import { GroundTeamUserRatingsSummaryDto } from '../../model/brokerConnections/IBrokerConnectionGroundTeamRatings';
import GroundTeamRatingsSummaryList from './GroundTeamRatingsSummaryList/GroundTeamRatingsSummaryList';
import UserRatingsDetailList from './UserRatingsDetailList/UserRatingsDetailList';
import DateFilter from './DateFilter/RatingsDateFilter';
import { Spinner } from '@fluentui/react-components';
import { useConversationRatingsPageStyles } from './ConversationRatingsPage.styles';
import {
    getAllUserRatings,
    RatingsFilterParams,
} from '../../store/features/groundTeamRatings/groundTeamRatingsApi';
import { useAppContext, LoadStatus } from '../../model';
import { app } from '@microsoft/teams-js';

const ConversationRatingsPage: React.FC = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const styles = useConversationRatingsPageStyles();
    const { teamsContext } = useAppContext();
    const installationTeamId = teamsContext?.team?.groupId;

    const [selectedUser, setSelectedUser] =
        useState<GroundTeamUserRatingsSummaryDto | null>(null);
    const [allUserRatings, setAllUserRatings] = useState<
        GroundTeamUserRatingsSummaryDto[]
    >([]);
    const [canViewAllTeams, setCanViewAllTeams] = useState(false);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('loading');

    const [dateFilter, setDateFilter] = useState<RatingsFilterParams>({
        utcStartDate: thirtyDaysAgo,
        utcEndDate: today,
        groundTeamIdFilter: undefined,
        installationTeamId: undefined,
    });

    const [appliedDateFilter, setAppliedDateFilter] = useState<RatingsFilterParams>({
        utcStartDate: thirtyDaysAgo,
        utcEndDate: today,
        groundTeamIdFilter: undefined,
        installationTeamId: undefined,
    });

    useEffect(() => {
        if (!installationTeamId) {
            return;
        }

        setDateFilter(prev => ({
            ...prev,
            installationTeamId,
        }));

        setAppliedDateFilter(prev => ({
            ...prev,
            installationTeamId,
        }));
    }, [installationTeamId]);

    useEffect(() => {
        if (!appliedDateFilter.installationTeamId) {
            return;
        }

        loadRatings(appliedDateFilter);
    }, [appliedDateFilter]);

    const loadRatings = async (filter: RatingsFilterParams) => {
        try {
            setLoadStatus('loading');

            const response = await getAllUserRatings(filter);
            setAllUserRatings(response.userSummaries);
            setCanViewAllTeams(response.canViewAllTeams);
            setLoadStatus('idle');
        } catch (err) {
            setLoadStatus('failed');
            console.error('Error loading ratings:', err);
        }
    };

    useEffect(() => {
        if (loadStatus === 'idle') {
            app.notifySuccess();
        }
    }, [loadStatus]);

    const handleUserSelect = (user: GroundTeamUserRatingsSummaryDto) => {
        setSelectedUser(user);
    };

    const handleBack = () => {
        setSelectedUser(null);
    };

    const handleApplyFilter = () => {
        setAppliedDateFilter({ ...dateFilter });
    };

    const handleClearFilter = () => {
        const today = new Date();
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);

        const defaultFilter = {
            utcStartDate: thirtyDaysAgo,
            utcEndDate: today,
            groundTeamIdFilter: undefined,
            installationTeamId,
        };

        setDateFilter(defaultFilter);
        setAppliedDateFilter(defaultFilter);
    };

    if (loadStatus === 'loading') {
        return (
            <div className={styles.root}>
                <div className={styles.centerContent}>
                    <Spinner label="Loading ratings data..." />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {selectedUser ? (
                <UserRatingsDetailList
                    user={selectedUser}
                    onBack={handleBack}
                    dateFilter={appliedDateFilter}
                />
            ) : (
                <>
                    <DateFilter
                        dateFilter={dateFilter}
                        onDateFilterChange={setDateFilter}
                        onApplyFilter={handleApplyFilter}
                        onClearFilter={handleClearFilter}
                        showTeamFilter={canViewAllTeams}
                    />
                    <GroundTeamRatingsSummaryList
                        users={allUserRatings}
                        onSelectUser={handleUserSelect}
                        loadStatus={loadStatus}
                    />
                </>
            )}
        </div>
    );
};

export default ConversationRatingsPage;
